import type { PAPEvent } from "@mirage/analytics/events/base/event";
import { PAP_Click_ChatChip } from "@mirage/analytics/events/types/click_chat_chip";
import type { ClickableEmptyChatInstructionsOption } from "@mirage/mosaics/Chat/components/chat/instructions/EmptyChatInstructions";
import { EmptyChatInstructions } from "@mirage/mosaics/Chat/components/chat/instructions/EmptyChatInstructions";
import { FSSEmptyChatInstructions } from "@mirage/mosaics/Chat/components/chat/instructions/FSSEmptyChatInstructions";
import { useFssDashChatUserInteraction } from "@mirage/mosaics/ConversationAssistant/hooks/useFssDashChatUserInteraction";
import { mapOptionToChatChip } from "@mirage/mosaics/ConversationAssistant/utils/chatChipMapping";
import { appendManualMessages } from "@mirage/service-conversation";
import type {
  Conversation,
  ConversationClientContext,
  UserMessageOptions,
} from "@mirage/service-conversation/types";
import { getDashActionSurfaceComponentLoggingSource } from "@mirage/service-conversation/utils/loggingUtils";
import { isEmbedPage } from "@mirage/shared/util/tiny-utils";
import i18n from "@mirage/translations";
import { useCallback } from "react";

export const StartingMessageNode = ({
  showEmptyChatInstructions,
  emptyState,
  logComposeEvent,
  openLibrary,
  setEmptyChatOption,
  conversationId,
  isConversationPageLoading,
  conversation,
  emptyChatOption,
  getWriteContextInputMessages,
  handleClickAddSources,
  navigateNewWorkflowAgent,
  setUserMessageOptions,
  setHideSuggestionsForCurrentAnswer,
  clientContext,
}: {
  showEmptyChatInstructions: boolean;
  emptyState: React.ReactNode;
  logComposeEvent: (event: PAPEvent) => void;
  openLibrary: () => void;
  setEmptyChatOption: (opt: ClickableEmptyChatInstructionsOption) => void;
  conversationId?: string;
  isConversationPageLoading: boolean;
  conversation?: Conversation;
  emptyChatOption?: ClickableEmptyChatInstructionsOption;
  getWriteContextInputMessages: (
    onClickAddSources: () => void,
  ) => React.ReactNode;
  handleClickAddSources: () => void;
  navigateNewWorkflowAgent: () => void;
  setUserMessageOptions: (options: UserMessageOptions) => void;
  setHideSuggestionsForCurrentAnswer: (hide: boolean) => void;
  clientContext?: ConversationClientContext;
}) => {
  const isEmbed = isEmbedPage();

  const { hasUsedFssDashChat, handleFssDashChatUserInteraction } =
    useFssDashChatUserInteraction(isEmbed, conversationId);

  const handlePostQuerySubmit = useCallback(
    (hideFollowUpSuggestions: boolean = false) => {
      setHideSuggestionsForCurrentAnswer(hideFollowUpSuggestions);
      handleFssDashChatUserInteraction();
    },
    [handleFssDashChatUserInteraction],
  );

  if (showEmptyChatInstructions) {
    if (isEmbed) {
      return (
        <FSSEmptyChatInstructions
          isFirstTimeChat={!hasUsedFssDashChat}
          clientContext={clientContext}
          handlePostQuerySubmit={handlePostQuerySubmit}
        />
      );
    }

    if (emptyState) {
      return <>{emptyState}</>;
    }
  } else {
    // transition for router redirects is fast enough that showing a spinner just shows another flash
    // instead of preventing the flash of the empty state content. loading state for hydration of an
    // existing conversation is already handled at the page level by isConversationLoading
    if (isConversationPageLoading && !conversation?.messages?.length) {
      return <></>;
    }
    // non-empty chat shows initial input messages if chat was started with Write
    if (emptyChatOption === "write") {
      return <>{getWriteContextInputMessages(handleClickAddSources)}</>;
    }
    return <></>;
  }

  return (
    <EmptyChatInstructions
      chatWithDashEnabled
      onClickOption={(opt: ClickableEmptyChatInstructionsOption) => {
        logComposeEvent(
          PAP_Click_ChatChip({
            chatChip: mapOptionToChatChip(opt),
          }),
        );
        if (opt === "library") {
          // Don't set emptyChatOption for library - just open the modal
          openLibrary();
        } else {
          setEmptyChatOption(opt);
          if (opt === "chat_with_dash") {
            if (!conversationId) {
              throw new Error(
                "Attempting to set option without conversationId",
              );
            }
            appendManualMessages([
              {
                conversationId: conversationId,
                message: i18n.t("assistant_initial_generic_prompt_message"),
                llmIgnore: true,
                messageType: "assistantMessage",
                source:
                  getDashActionSurfaceComponentLoggingSource(
                    "compose_chat_pane",
                  ),
              },
            ]);
          } else if (opt === "write") {
            setUserMessageOptions({ agentIds: ["write"] });
          } else if (opt === "create-ai-tool") {
            navigateNewWorkflowAgent();
          }
        }
      }}
    />
  );
};
